import type { IsWalkable, TilePoint } from './mobile';
import { dungeonSourceKey, type DungeonBlueprint } from './dungeon';
import { isDungeonStructureRole, type DungeonStructureRole } from './dungeonModule';

/**
 * The serialisable shape of a game map: this project's own simple format, *not*
 * UO's map files. A map is a flat (M2: no per-tile elevation yet) grid of land
 * tiles drawn from a small `palette` of tile names, plus a parallel walkability
 * mask. Stored row-major, so the cell for (x, y) is at index `y * width + x`.
 *
 * Keeping this a plain data interface (no methods, no class) means a map can be
 * authored as JSON, generated, or sent over a wire untouched; {@link GameMap}
 * wraps it with the accessors the sim and renderer actually call.
 */
/**
 * A single static object placed in the world: a flat billboard (tree, wall, ...)
 * sitting at a tile coordinate. `tileName` names a baked static art (the render
 * layer resolves it to a texture, the way land tile names resolve today); `x`/`y`
 * are tile coordinates and `z` is elevation. `z` is carried even though land is
 * flat (the M2 decision) because depth sorting needs it. Multiple statics may
 * share a tile. Statics do *not* affect walkability in this milestone.
 */
export interface StaticPlacement {
  tileName: string;
  x: number;
  y: number;
  z: number;
  /**
   * Whether this static is a roof piece, derived offline from the real UO
   * `tiledata` ROOF flag during extraction. Absent/false for everything else.
   * It is the only thing the client's per-viewer roof-hiding keys off, so the
   * decision stays a pure data property of the map rather than a render-time
   * guess; the shared world is identical for everyone and only what each browser
   * *draws* differs. See `src/render/roofHiding.ts` and ADR-0003.
   */
  roof?: boolean;
  /** Editor-authored semantic role used by a dungeon structure kit. */
  structureRole?: DungeonStructureRole;
  /** ClassicUO light admission treats this art as see-through overhead. */
  lightTransparent?: true;
  /**
   * ClassicUO-compatible light emitted by this placed static.
   * `profile` is the static tile's layer byte, which selects the light shape.
   * `color` is an RGB value derived from ClassicUO's graphic color table.
   */
  light?: {
    profile: number;
    color: number;
    /** ClassicUO default light-shader row, retained for curve-faithful rendering. */
    shader?: number;
  };
}

/** Validate persisted static placement shape, bounds, and semantic structure metadata. */
export function validateStaticPlacements(
  value: unknown,
  width: number,
  height: number,
): string[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return ['statics is not an array'];
  const errors: string[] = [];
  value.forEach((candidate, index) => {
    if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) {
      errors.push(`statics[${index}] is not a static placement`);
      return;
    }
    const item = candidate as Record<string, unknown>;
    if (typeof item.tileName !== 'string' || item.tileName.length === 0) {
      errors.push(`statics[${index}].tileName is not a tile name`);
    }
    if (
      !Number.isInteger(item.x) ||
      !Number.isInteger(item.y) ||
      !Number.isInteger(item.z) ||
      (item.x as number) < 0 ||
      (item.y as number) < 0 ||
      (item.x as number) >= width ||
      (item.y as number) >= height
    ) {
      errors.push(
        `static "${String(item.tileName)}" out of bounds at ` +
        `(${String(item.x)}, ${String(item.y)}, z=${String(item.z)})`,
      );
    }
    if (item.roof !== undefined && typeof item.roof !== 'boolean') {
      errors.push(`statics[${index}].roof is not a boolean`);
    }
    if (item.structureRole !== undefined && !isDungeonStructureRole(item.structureRole)) {
      errors.push(`statics[${index}].structureRole is invalid`);
    }
    if (item.lightTransparent !== undefined && item.lightTransparent !== true) {
      errors.push(`statics[${index}].lightTransparent is invalid`);
    }
    if (item.light !== undefined) {
      const light = item.light as Record<string, unknown>;
      if (
        typeof light !== 'object' ||
        light === null ||
        !Number.isInteger(light.profile) ||
        (light.profile as number) < 0 ||
        (light.profile as number) >= 100 ||
        !Number.isInteger(light.color) ||
        (light.color as number) < 0 ||
        (light.color as number) > 0xffffff ||
        (
          light.shader !== undefined &&
          (
            !Number.isInteger(light.shader) ||
            (light.shader as number) < 0 ||
            (light.shader as number) > 63
          )
        )
      ) {
        errors.push(`statics[${index}].light is invalid`);
      }
    }
  });
  return errors;
}

export interface MapData {
  width: number;
  height: number;
  /** Land tile names this map uses; `cells` indexes into this list. */
  palette: string[];
  /** Row-major palette indices, length `width * height`. */
  cells: number[];
  /** Row-major impassability mask, length `width * height`; true = blocked. */
  blocked: boolean[];
  /**
   * Optional per-tile land elevation (true terrain height, PRD #536): row-major
   * signed integers, length `width * height`, in the UO-conventional signed-byte
   * range {@link MIN_LAND_Z}..{@link MAX_LAND_Z}. **Absent means flat** - every
   * tile at z 0, byte-for-byte today's behavior - so existing maps (`wbb.json`
   * included) load and replicate unchanged. Same shape family as
   * `cells`/`blocked`. Open-plain maps are gridless by contract and never carry
   * heights. Read through {@link GameMap.landZ}; step legality across height
   * edges is the climb rule ({@link GameMap.canStep}, {@link CLIMB}).
   */
  heights?: number[];
  /**
   * Optional list of static objects placed on the map. Sparse - placements name
   * their art directly by string rather than via a palette. Absent means none;
   * existing land-only maps load unchanged.
   */
  statics?: StaticPlacement[];
  /**
   * Optional authored tile every player spawns on (world-absolute). Travels with
   * the map so a client can frame its pre-login / behind-modal spectator view on
   * the exact spawn tile - no camera jump when the player enters (issue #193).
   * Additive-optional: maps without it (older saves, the generated test grid) load
   * unchanged and callers fall back to a crowd centroid.
   */
  playerSpawn?: TilePoint;
  /**
   * Semantic layout metadata for a generated dungeon floor.
   *
   * The complete geometry still lives in the ordinary immutable map fields;
   * this additive descriptor lets validation, reveal-state presentation, and
   * exit detection use authored room meaning without reverse-engineering tiles.
   */
  dungeon?: DungeonBlueprint;
  /**
   * Presentation volume for an authored underground map that does not carry a
   * generated-dungeon blueprint.
   */
  lighting?: 'dungeon';
  /**
   * **Open-plain mode** (the endless Arena field, PRD #387 Slice C): the map is a
   * virtual, unbounded plain - `isWalkable`/`inBounds` are true for EVERY
   * coordinate (negative included), no tile grid exists (`cells`/`blocked` are
   * empty and `width`/`height` are only a nominal size around the spawn), and the
   * ground resolves per tile from the deterministic {@link plainVariant} hash over
   * the `palette` - evaluated on demand at render/lookup time, so there is nothing
   * to store or ship. This whole object is the "sentinel descriptor" that rides
   * the wire in place of a grid (~a hundred bytes instead of the old
   * width*height*2 arrays). Additive-optional: grid maps (the town, saves) never
   * carry it and load byte-identically.
   */
  plain?: true;
}

/**
 * Recursively freeze a {@link MapData} so its arrays and static placements can
 * never be mutated after a {@link GameMap} wraps it. This is what makes the
 * by-reference {@link GameMap.toData} (and therefore the persistence snapshot,
 * `serialize()` in `persistence.ts`) provably safe: the snapshot aliases the live
 * map, but the live map is immutable, so the alias can never observe a later
 * mutation - the contract the rest of the codebase already relies on, now enforced
 * by the runtime rather than merely assumed. The map is built once (at boot, or
 * from a fresh JSON-parsed save) and never edited, so this one-time cost is off
 * every hot path; freezing primitives in `cells`/`blocked` is a no-op.
 */
function freezeMapData(data: MapData): MapData {
  Object.freeze(data.palette);
  Object.freeze(data.cells);
  Object.freeze(data.blocked);
  if (data.heights) Object.freeze(data.heights);
  if (data.statics) {
    for (const s of data.statics) {
      if (s.structureRole) Object.freeze(s.structureRole);
      if (s.light) Object.freeze(s.light);
      Object.freeze(s);
    }
    Object.freeze(data.statics);
  }
  if (data.playerSpawn) Object.freeze(data.playerSpawn);
  if (data.dungeon) {
    for (const room of data.dungeon.rooms) {
      Object.freeze(room.grid);
      Object.freeze(room.region);
      Object.freeze(room.revealRegion);
      if (room.revealRegions) {
        for (const region of room.revealRegions) Object.freeze(region);
        Object.freeze(room.revealRegions);
      }
      if (room.footprint) {
        for (const point of room.footprint) Object.freeze(point);
        Object.freeze(room.footprint);
      }
      if (room.clearanceHalo) {
        for (const point of room.clearanceHalo) Object.freeze(point);
        Object.freeze(room.clearanceHalo);
      }
      for (const zone of room.reservedZones) Object.freeze(zone);
      Object.freeze(room.reservedZones);
      if (room.encounterZones) {
        for (const zone of room.encounterZones) Object.freeze(zone);
        Object.freeze(room.encounterZones);
      }
      Object.freeze(room.transform);
      Object.freeze(room);
    }
    for (const connection of data.dungeon.connections) {
      Object.freeze(connection.region);
      Object.freeze(connection.transform);
      Object.freeze(connection.a.position);
      Object.freeze(connection.a);
      Object.freeze(connection.b.position);
      Object.freeze(connection.b);
      Object.freeze(connection.revealRegion);
      if (connection.revealRegions) {
        for (const region of connection.revealRegions) Object.freeze(region);
        Object.freeze(connection.revealRegions);
      }
      if (connection.segments) {
        for (const segment of connection.segments) {
          Object.freeze(segment.from);
          Object.freeze(segment.to);
          Object.freeze(segment.transform);
          Object.freeze(segment.region);
          Object.freeze(segment);
        }
        Object.freeze(connection.segments);
      }
      if (connection.footprint) {
        for (const point of connection.footprint) Object.freeze(point);
        Object.freeze(connection.footprint);
      }
      if (connection.clearanceHalo) {
        for (const point of connection.clearanceHalo) Object.freeze(point);
        Object.freeze(connection.clearanceHalo);
      }
      if (connection.door) Object.freeze(connection.door);
      Object.freeze(connection);
    }
    Object.freeze(data.dungeon.rooms);
    Object.freeze(data.dungeon.connections);
    Object.freeze(data.dungeon.entrance.position);
    Object.freeze(data.dungeon.entrance);
    Object.freeze(data.dungeon.exit.position);
    Object.freeze(data.dungeon.exit);
    for (const landmark of data.dungeon.landmarks) {
      Object.freeze(landmark.position);
      Object.freeze(landmark);
    }
    Object.freeze(data.dungeon.landmarks);
    Object.freeze(data.dungeon);
  }
  return Object.freeze(data);
}

/**
 * The climb rule's threshold (true terrain height, PRD #536): a step from tile A
 * to tile B is legal iff `|landZ(B) - landZ(A)| <= CLIMB`. UO-conventional small
 * delta - slopes are sequences of legal steps, and a steeper edge is a cliff
 * (blocked in **both** directions; dropping off is as illegal as scaling). The
 * rule is per-edge, so it lives in {@link GameMap.canStep} / the sim's
 * `CanStep` seam, never in the 2-arg `IsWalkable`.
 */
export const CLIMB = 2;

/** The legal `heights` value range: UO's signed-byte land z. */
export const MIN_LAND_Z = -128;
export const MAX_LAND_Z = 127;

/**
 * A grid cell that participates in bounds and collision but has no rendered
 * land surface. Private procedural maps use it outside authored footprints.
 */
export const MAP_VOID_TILE = '__void__';

/**
 * Deterministic per-tile pick of a palette index for an **open-plain** map: a
 * fixed integer hash of the coordinates (an avalanche mix - never the seeded
 * rng), so the endless field reads as natural mottled ground while every
 * client and the server agree byte-for-byte at ANY coordinate, with zero
 * storage. This is the hash PR #384 introduced for the walled arena's grass;
 * it lives here now so the map's `tileNameAt` and the renderer's procedural
 * ground draw from one source.
 */
export function plainVariant(x: number, y: number, paletteSize: number): number {
  let h = Math.imul(x, 0x9e3779b1) ^ Math.imul(y, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) % paletteSize;
}

/**
 * A validated, read-only view over {@link MapData} with the accessors the sim
 * (walkability) and renderer (tile lookup) need. It imports no rendering and no
 * Pixi, so it stays a pure, testable unit on the sim side of the hard split.
 *
 * The wrapped {@link MapData} is deep-frozen on construction (see
 * {@link freezeMapData}), so a map is immutable for its whole life: this is the
 * invariant that lets {@link toData} hand the backing data out by reference (no
 * defensive copy on the hot persistence path) without any aliasing risk.
 */
export class GameMap {
  readonly width: number;
  readonly height: number;
  /**
   * Cells that have a roof static, keyed `"x,y,z"` - the walkable surface of
   * every roof plate. Built once at construction (the map is immutable), so the
   * per-step {@link isRoofWalkable} probe stays O(1). See `walkability.ts` for
   * why a roof plate *is* the elevated level's walkability: a tile above ground
   * is standable exactly where a roof piece exists at that z, and nowhere else,
   * so the plate's edge self-blocks and the ground floor's walls/doors never
   * leak upward.
   */
  private readonly roofCells: Set<string>;
  /**
   * The highest `z` of any **non-roof** static per tile, keyed `"x,y"`. This is
   * what keeps a roof walker off the perimeter wall tops: on the building's
   * near (south/east) faces the wall course reaches the roof's exact `z` and
   * shares its tile with a roof piece (the parapet-vs-roof-cap tie depth.ts
   * also resolves), so a roof cell is standable only when every non-roof
   * static on its tile stays *below* the level. Interior walls top out at
   * ground `z`, far below the plate, so they remain irrelevant up there.
   */
  private readonly staticTopByTile: Map<string, number>;
  /**
   * The authored tile every player spawns on, or undefined for maps that declare
   * none (the generated test grid). Frozen with the rest of the map data, so it is
   * safe to expose by reference. Used by the client to frame its behind-modal
   * spectator camera on the exact spawn tile (issue #193).
   */
  readonly playerSpawn?: TilePoint;

  /**
   * Whether this is an unbounded open-plain map ({@link MapData.plain}):
   * all-walkable at every coordinate, ground resolved per tile from
   * {@link plainVariant} over the palette, no grid.
   */
  readonly isPlain: boolean;

  constructor(private readonly data: MapData) {
    const expected = data.width * data.height;
    if (data.width <= 0 || data.height <= 0) {
      throw new Error(`map size must be positive, got ${data.width}x${data.height}`);
    }
    if (data.lighting !== undefined && data.lighting !== 'dungeon') {
      throw new Error(`unknown map lighting mode '${String(data.lighting)}'`);
    }
    if (data.plain) {
      // An open plain carries no grid: the arrays must be empty (a non-empty
      // grid alongside the flag would be two competing sources of truth) and
      // the palette non-empty (every tile draws from it).
      if (data.cells.length !== 0 || data.blocked.length !== 0) {
        throw new Error('an open-plain map must carry empty cells/blocked arrays');
      }
      if (data.palette.length === 0) {
        throw new Error('an open-plain map needs a non-empty palette');
      }
      if (data.heights) {
        throw new Error('an open-plain map is gridless and cannot carry heights');
      }
    } else {
      if (data.cells.length !== expected) {
        throw new Error(`cells length ${data.cells.length} != ${expected} (${data.width}x${data.height})`);
      }
      if (data.blocked.length !== expected) {
        throw new Error(`blocked length ${data.blocked.length} != ${expected} (${data.width}x${data.height})`);
      }
      for (const cell of data.cells) {
        if (cell < 0 || cell >= data.palette.length) {
          throw new Error(`cell palette index ${cell} out of range 0..${data.palette.length - 1}`);
        }
      }
      if (data.heights) {
        if (data.heights.length !== expected) {
          throw new Error(
            `heights length ${data.heights.length} != ${expected} (${data.width}x${data.height})`,
          );
        }
        for (const h of data.heights) {
          if (!Number.isInteger(h) || h < MIN_LAND_Z || h > MAX_LAND_Z) {
            throw new Error(`height ${h} must be an integer in ${MIN_LAND_Z}..${MAX_LAND_Z}`);
          }
        }
      }
    }
    this.isPlain = data.plain === true;
    this.width = data.width;
    this.height = data.height;
    this.playerSpawn = data.playerSpawn;
    this.roofCells = new Set();
    this.staticTopByTile = new Map();
    for (const s of data.statics ?? []) {
      if (!this.inBounds(s.x, s.y)) {
        throw new Error(`static '${s.tileName}' out of bounds at (${s.x}, ${s.y})`);
      }
      if (s.roof) {
        this.roofCells.add(`${s.x},${s.y},${s.z}`);
      } else {
        const k = `${s.x},${s.y}`;
        this.staticTopByTile.set(k, Math.max(this.staticTopByTile.get(k) ?? -Infinity, s.z));
      }
    }
    // Lock the map down once validated: a GameMap is read-only for its whole life,
    // so the by-reference `toData()` snapshot can never observe a later mutation.
    freezeMapData(data);
  }

  /**
   * The backing {@link MapData} this map wraps, for serialization. Returns the
   * live data **by reference, not a copy** - deliberately, so the persistence hot
   * path (`serialize()` builds a keyframe from this every cadence) pays nothing.
   * That is safe only because the data is deep-frozen on construction (see
   * {@link freezeMapData}): the snapshot aliases the live map, but neither side can
   * mutate it, so the alias is harmless. A deserialized map is rebuilt from a
   * fresh, JSON-parsed copy, so saves stay independent across loads.
   */
  toData(): MapData {
    return this.data;
  }

  inBounds(x: number, y: number): boolean {
    if (this.isPlain) return true; // an open plain has no edges
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  /** The land tile name at (x, y), or undefined when off the map. */
  tileNameAt(x: number, y: number): string | undefined {
    if (this.isPlain) {
      return this.data.palette[plainVariant(x, y, this.data.palette.length)];
    }
    if (!this.inBounds(x, y)) return undefined;
    const tileName = this.data.palette[this.data.cells[y * this.width + x]!];
    return tileName === MAP_VOID_TILE ? undefined : tileName;
  }

  /** Whether (x, y) may be stepped onto: on the map and not blocked. */
  isWalkable(x: number, y: number): boolean {
    if (this.isPlain) return true; // walkability is true everywhere on the plain
    if (!this.inBounds(x, y)) return false;
    return !this.data.blocked[y * this.width + x];
  }

  /** An {@link IsWalkable} bound to this map, ready to hand to a {@link Mobile}. */
  walkableFn(): IsWalkable {
    return (x, y) => this.isWalkable(x, y);
  }

  /**
   * The land elevation at (x, y): the per-tile `heights` value, or 0 when the
   * map is flat (no `heights` field), an open plain, or the coordinate is off
   * the map. Ground z is **derived** from this function - never authored entity
   * state; the movement system mirrors it into `position.z` on every committed
   * ground-level step (true terrain height, PRD #536 Slice A).
   */
  /**
   * Whether the map carries a `heights` array at all (true terrain height,
   * PRD #536). Lets a consumer pick a flat fast path (e.g. the sloped-mesh
   * renderer keeps the sprite ground on flat maps, Slice E) without probing
   * every tile. Open plains never carry heights (refused at validation).
   */
  get hasHeights(): boolean {
    return this.data.heights !== undefined;
  }

  landZ(x: number, y: number): number {
    const heights = this.data.heights;
    if (!heights || !this.inBounds(x, y)) return 0;
    return heights[y * this.width + x]!;
  }

  /**
   * The climb rule: whether the height edge from tile `from` to tile `to` is
   * legal - `|landZ(to) - landZ(from)| <= CLIMB`. Per-edge (it cannot be
   * expressed in the 2-arg {@link IsWalkable}), and **height only**: it says
   * nothing about the destination cell's walkability - `walkability.ts`'s
   * `canStepForLevel` composes the two. On a flat map (no `heights`) it
   * degenerates to always-true, so composing it changes nothing.
   */
  canStep(from: TilePoint, to: TilePoint): boolean {
    if (!this.data.heights) return true;
    return Math.abs(this.landZ(to.x, to.y) - this.landZ(from.x, from.y)) <= CLIMB;
  }

  /**
   * The **level** an entity at tile (x, y) with `position.z` z occupies (true
   * terrain height, PRD #536 Slice B). `position.z` is a discrete level
   * selector reinterpreted by Slice A: an entity is on the **ground level 0**
   * when its z is 0 (the flat default, incl. absent) or equals the terrain
   * height under its tile (the movement system's mirrored `landZ`); anything
   * else is a roof-plate level identified by its own z. Every consumer keying
   * or branching on "which level" (teleporter cells, walk validation, pursuit
   * same-level checks, `walkableForLevel`) must normalize through this one
   * helper so a hill walker carrying a mirrored z=3 is never misread as a
   * roof-plate walker. On a flat map it degenerates to `z ?? 0`.
   */
  levelAt(x: number, y: number, z: number | undefined): number {
    const zz = z ?? 0;
    if (zz === 0 || zz === this.landZ(x, y)) return 0;
    return zz;
  }

  /**
   * Resolve a destination **level** to the concrete z to write into
   * `position.z` (the inverse of {@link levelAt}): the ground level 0 lands on
   * the terrain height at (x, y), a roof-plate level keeps its own z. Every
   * server-side spawn/teleport that used to write a literal `pos.z = 0`
   * ("ground plane") goes through this so placed entities land ON the terrain
   * instead of inside a hill. Flat maps: identical to the old literal 0.
   */
  groundedZ(x: number, y: number, z: number): number {
    return z === 0 ? this.landZ(x, y) : z;
  }

  /**
   * Whether (x, y) is standable at the elevated level `z`: a roof static exists
   * at that tile and z, AND every non-roof static on the tile stays below the
   * level. The ground's `blocked` mask (interior walls, doors) deliberately
   * plays no part - interior walls top out at ground z and stay irrelevant -
   * but the perimeter wall course, which rises to the roof's own z on the
   * building's near faces, blocks its tile so a roof walker cannot stand on
   * the wall top. A step past the plate's edge finds no roof cell and is
   * refused.
   */
  isRoofWalkable(x: number, y: number, z: number): boolean {
    if (!this.roofCells.has(`${x},${y},${z}`)) return false;
    return (this.staticTopByTile.get(`${x},${y}`) ?? -Infinity) < z;
  }

  /**
   * The static placements on this map. Returns a fresh array (a defensive copy)
   * so callers - notably the renderer - cannot mutate the map's internal state.
   * An absent `statics` field yields an empty list.
   */
  getStatics(): StaticPlacement[] {
    return [...(this.data.statics ?? [])];
  }
}

/**
 * Whether two maps describe the **same terrain** - the change detector behind the
 * client's mid-session scene swap (Arena Slice E, PRD #334 / ADR-0177).
 *
 * Object identity is useless for this: every map-bearing `Snapshot` the client
 * adopts JSON-parses a **fresh** {@link MapData} (and the per-MapData GameMap
 * cache builds a fresh `GameMap` from it), so a reconnect's town snapshot and an
 * Arena entry's arena snapshot *both* change identity - but only the arena one
 * should rebuild the terrain/statics scene. So compare content: dimensions, the
 * palette, the land cells, the blocked mask, and **each static placement**
 * (tileName/x/y/z/roof, positionally - authored statics are ordered data, so a
 * deploy that merely *moves* one still reads as a different map and refreshes a
 * reconnecting client's scene rather than leaving it stale). Cheap enough to be
 * free (it runs only when a map-bearing full snapshot arrives - connect,
 * reconnect, arena entry/exit - never per delta, and short-circuits on the first
 * difference; the arena's 80x80 differs from the town map on `width` alone).
 */
export function sameMapContent(a: GameMap, b: GameMap): boolean {
  if (a === b) return true;
  const da = a.toData();
  const db = b.toData();
  if (da === db) return true;
  // The plain flag is part of the terrain's identity: a gridless plain never
  // matches a grid map (town <-> arena swaps on this alone), and two plains
  // compare on the remaining fields (palette; the grid loops are vacuous).
  if ((da.plain ?? false) !== (db.plain ?? false)) return false;
  if (da.width !== db.width || da.height !== db.height) return false;
  if ((da.dungeon === undefined) !== (db.dungeon === undefined)) return false;
  if (da.lighting !== db.lighting) return false;
  if (
    da.dungeon &&
    db.dungeon &&
    dungeonSourceKey(da.dungeon) !== dungeonSourceKey(db.dungeon)
  ) {
    return false;
  }
  const sa = da.statics ?? [];
  const sb = db.statics ?? [];
  if (sa.length !== sb.length) return false;
  for (let i = 0; i < sa.length; i++) {
    const p = sa[i]!;
    const q = sb[i]!;
    if (
      p.tileName !== q.tileName ||
      p.x !== q.x ||
      p.y !== q.y ||
      p.z !== q.z ||
      (p.roof ?? false) !== (q.roof ?? false) ||
      (p.lightTransparent ?? false) !== (q.lightTransparent ?? false) ||
      JSON.stringify(p.light) !== JSON.stringify(q.light) ||
      JSON.stringify(p.structureRole) !== JSON.stringify(q.structureRole)
    ) {
      return false;
    }
  }
  if (da.palette.length !== db.palette.length) return false;
  for (let i = 0; i < da.palette.length; i++) {
    if (da.palette[i] !== db.palette[i]) return false;
  }
  for (let i = 0; i < da.cells.length; i++) {
    if (da.cells[i] !== db.cells[i]) return false;
  }
  for (let i = 0; i < da.blocked.length; i++) {
    if (da.blocked[i] !== db.blocked[i]) return false;
  }
  // Terrain height is part of the terrain's identity (PRD #536): a heights
  // field appearing/vanishing or any per-tile change is a different map. Flat
  // maps (both absent) compare equal with zero extra work.
  const ha = da.heights;
  const hb = db.heights;
  if ((ha === undefined) !== (hb === undefined)) return false;
  if (ha && hb) {
    for (let i = 0; i < ha.length; i++) {
      if (ha[i] !== hb[i]) return false;
    }
  }
  return true;
}
